import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { Button, InputGroup, ListGroup, FormControl, Tabs, Tab, Col, Nav, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import ToastComponent from '../Toast/Toast';
import SigningProcessModal from '../Modals/SigningProcessModal';
import RejectingProcessModal from '../Modals/RejectingProcessModal';
import LoadingCircle from '../LoadingCircle';
import { CustomModal } from '../CustomModal/CustomModal';
import iconAttachedDocs from '../../assets/icons/attachedDocs.svg';
import iconBackButton from '../../assets/icons/backButton.svg';
import iconNextButton from '../../assets/icons/nextButton.svg';
import squareEyeBox from '../../assets/icons/square-eye.svg';
import squareDocBox from '../../assets/icons/square-doc.svg';
import { REJECTION_REASON_MIN_CHARACTERS } from '../../assets/constants';
import Utils from '../../services/Utils';
import ApprovalService from '../../services/Approval/ApprovalService';
import './ApprovalPreview.css';

export const ApprovalPreview = (props) => {
  const {
    renderedApproval,
    selectedApproval,
    pendingApprovals,
    documentSigners,
    documentAttatchmentsList,
    attDocArray,
    docsAttachedRendered,
    totalPages,
    checkedApprovals,
    updateSelectedApprovals,
    viewApprovalHandler,
    seeNextApproval,
    showApprovedDocHandler,
    showRejectedDocHandler,
    handlePreviouslyRejectedDocError,
    renderDocument,
    t,
  } = props;

  const [showSignModal, setShowSignModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [time, setTime] = useState(0);
  const [loadingApprovalProcess, setLoadingApprovalProcess] = useState(false);
  const [loadingRejectingProcess, setLoadingRejectingProcess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorData, setErrorData] = useState('');
  const [errorType, setErrorType] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actualPage, setActualPage] = useState(1);
  const [actualAttachedPage, setActualAttachedPage] = useState(1);
  const [tabKey, setTabKey] = useState(0);
  const [approvedDoc, setApprovedDoc] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [certificatesList] = useState(undefined);

  const timerRef = useRef(null);

  const handleClose = useCallback(() => {
    setShowSignModal(false);
    setShowRejectModal(false);
  }, []);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setTime((tPrev) => tPrev + 1);
    }, 60000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const doLogOut = useCallback(() => {
    sessionStorage.clear();
    stopTimer();
  }, [stopTimer]);

  const actualPageHandler = useCallback(() => {
    const renderedPages = renderedApproval.length;
    const container = document.getElementsByClassName('pdf-container')[0];
    if (!container || renderedPages === 0) return;

    const imageHeight = document.getElementsByClassName('pdf-images')[0]?.offsetHeight ?? 0;
    const scrollHeight = imageHeight * renderedPages;
    const containerHeight = container.offsetHeight;
    const scrollPosition = container.scrollTop;
    const scrollMaxValue = scrollHeight - containerHeight || 1;
    const scrollPercentage = Math.abs((scrollPosition / scrollMaxValue) * 100);
    const pageRatio = 100 / renderedPages;
    const actualPageNum = scrollPercentage / pageRatio;
    const maxPageRatio = 100 / pageRatio;

    let actualPageAbsolute = 1 + Math.floor(actualPageNum);
    if (actualPageAbsolute >= maxPageRatio) {
      actualPageAbsolute = renderedPages;
    }

    if (actualPageAbsolute >= renderedPages - 1) {
      renderDocument();
    }

    setActualPage(actualPageAbsolute);
  }, [renderedApproval, renderDocument]);

  const actualAttachedPageHandler = useCallback(() => {
    const container = document.getElementsByClassName('pdf-container-2')[0];
    if (!container || !docsAttachedRendered?.length) return;

    const imageHeight = document.getElementsByClassName('attached-pdf-images')[0]?.offsetHeight ?? 0;
    const scrollHeight = imageHeight * docsAttachedRendered.length;
    const containerHeight = container.offsetHeight;
    const scrollPosition = container.scrollTop;
    const scrollMaxValue = scrollHeight - containerHeight || 1;
    const scrollPercentage = Math.abs((scrollPosition / scrollMaxValue) * 100);
    const pageRatio = 100 / docsAttachedRendered.length;
    const actualPageNum = scrollPercentage / pageRatio;
    const maxPageRatio = 100 / pageRatio;

    let actualPageAbsolute = 1 + Math.floor(actualPageNum);
    if (actualPageAbsolute >= maxPageRatio) {
      actualPageAbsolute = docsAttachedRendered.length;
    }

    setActualAttachedPage(actualPageAbsolute);
  }, [docsAttachedRendered]);

  const changeAttachedTab = useCallback((k) => {
    setTabKey(k);
    setActualAttachedPage(1);
    const container = document.getElementsByClassName('pdf-container-2')[0];
    if (container) {
      container.scrollTop = 0;
    }
  }, []);

  const rejectDoc = useCallback(() => {
    const { ApproverGuid, DocGuid } = selectedApproval;
    setLoadingRejectingProcess(true);
    setShowRejectModal(false);

    const rejectBody = { RejectedReason: rejectReason };

    ApprovalService.rejectApproval(ApproverGuid, rejectBody)
      .then((res) => {
        if (res.status === 200) {
          Utils.removeItemFromSelectedDocsInSessionByGuid(DocGuid);
          setLoadingRejectingProcess(false);
          showRejectedDocHandler();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          setSessionExpired(true);
          startTimer();
        } else if (error.response) {
          setErrorType(error.response.status);
          setErrorData(error.response.data);
          setShowError(true);
          setLoadingRejectingProcess(false);
        }
        handlePreviouslyRejectedDocError(error, true);
      });
  }, [
    selectedApproval,
    rejectReason,
    showRejectedDocHandler,
    startTimer,
    handlePreviouslyRejectedDocError,
  ]);

  const handleSign = useCallback(() => {
    const { ApproverGuid, DocGuid } = selectedApproval;
    setLoadingApprovalProcess(true);

    const body = {};

    ApprovalService.acceptApproval(ApproverGuid, body)
      .then((res) => {
        if (res.status === 200) {
          Utils.removeItemFromSelectedDocsInSessionByGuid(DocGuid);
          setLoadingApprovalProcess(false);
          setApprovedDoc(true);
          showApprovedDocHandler();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          setSessionExpired(true);
          startTimer();
        } else if (error.response) {
          setErrorType(error.response.status);
          setErrorData(error.response.data);
          setShowError(true);
          setLoadingApprovalProcess(false);
        }
        handlePreviouslyRejectedDocError(error, true);
      });
  }, [selectedApproval, showApprovedDocHandler, startTimer, handlePreviouslyRejectedDocError]);

  // Scripts
  useEffect(() => {
    Utils.appendScript('/assets/js/autoscript.js');
    Utils.appendScript('/assets/js/forge.min.js');
  }, []);

  // Scroll listeners when DOM is available
  useEffect(() => {
    if (renderedApproval.length === 0) return;
    const pdfContainer = document.getElementsByClassName('pdf-container')[0];
    if (!pdfContainer) return;
    pdfContainer.addEventListener('scroll', actualPageHandler);
    return () => {
      pdfContainer.removeEventListener('scroll', actualPageHandler);
    };
  }, [renderedApproval, actualPageHandler]);

  useEffect(() => {
    if (documentAttatchmentsList.length === 0) return;
    const pdfContainer2 = document.getElementsByClassName('pdf-container-2')[0];
    if (!pdfContainer2) return;
    pdfContainer2.addEventListener('scroll', actualAttachedPageHandler);
    return () => {
      pdfContainer2.removeEventListener('scroll', actualAttachedPageHandler);
    };
  }, [documentAttatchmentsList, actualAttachedPageHandler]);

  const isApprovalSelected = checkedApprovals?.includes(selectedApproval?.ApproverGuid);

  const images = renderedApproval.map((renderedImage, index) => (
    <img
      key={index}
      alt="renderedImage"
      src={`data:image/jpeg;base64,${renderedImage}`}
      className="pdf-images"
      id={`pdf-page-${index + 1}`}
    />
  ));

  const attachedImages = docsAttachedRendered.map((renderedImage, index) => (
    <img
      key={index}
      alt="attachedRenderedImage"
      src={`data:image/jpeg;base64,${renderedImage}`}
      className="attached-pdf-images"
    />
  ));

  const onCheck = () => {
    updateSelectedApprovals(selectedApproval, !isApprovalSelected);
  };

  const language = localStorage.getItem('i18nextLng');
  const timeText =
    language === 'es'
      ? `Hace ${time} minutos`
      : language === 'en'
      ? `${time} minutes ago`
      : language === 'ca'
      ? `Fa ${time} minuts`
      : `Hace ${time} minutos`;

  const columns = [
    {
      dataField: 'SignerName',
      text: t('documents.document-signers-table-name'),
      align: 'left',
      headerAlign: 'left',
    },
    {
      dataField: 'UploadTime',
      text: t('documents.document-signers-table-delivery-date'),
      align: 'left',
      headerAlign: 'left',
    },
    {
      dataField: 'OperationTime',
      text: t('documents.document-signers-table-signature-date'),
      align: 'left',
      headerAlign: 'left',
    },
    {
      dataField: 'SignatureStatus',
      text: t('documents.document-signers-table-status'),
      headerAlign: 'center',
      formatter: (cellContent) => {
        if (cellContent !== null) {
          if (cellContent === 'Signed') return <div className="signedCellbox">{cellContent}</div>;
          if (cellContent === 'Unsigned') return <div className="unsignedCellbox">{cellContent}</div>;
          if (cellContent === 'Rejected') return <div className="rejectedCellbox">{cellContent}</div>;
        }
        return <div className="undefinedCellbox">Undefined</div>;
      },
    },
  ];

  const isInvalidRejectionReason = rejectReason.length < REJECTION_REASON_MIN_CHARACTERS;

  const docIndex = pendingApprovals.findIndex((doc) => doc.DocGuid === selectedApproval.DocGuid);
  const isLastIndex = docIndex === pendingApprovals.length - 1;

  return (
    <div>
      <ToastComponent
        onClose={() => setApprovedDoc(false)}
        show={approvedDoc}
        autohide
        delay={3000}
        title="Documento aprobado"
        message="Documento aprobado con éxito"
        variant="success"
        zIndex={10}
      />
      {showError && (
        <ToastComponent
          onClose={() => setShowError(false)}
          show={showError}
          title={
            <>
              {t('general.error')} {errorType}
            </>
          }
          message={errorData}
          variant="error"
          closeButton
          zIndex={10}
        />
      )}
      {sessionExpired && (
        <ToastComponent
          show={sessionExpired}
          title={t('general.notification')}
          subtitle={timeText}
          variant="session-expired"
          zIndex={10}
          closeButton={false}
        >
          <p>{t('general.session-expired')}</p>
          <Button
            type="button"
            className="btn btn-primary"
            id="sign-all-docs"
            onClick={doLogOut}
          >
            {t('general.accept')}
          </Button>
        </ToastComponent>
      )}
      {renderedApproval.length === 0 && <LoadingCircle centerVertically />}
      {renderedApproval.length > 0 && (
        <div>
          <div className="back-container" onClick={viewApprovalHandler}>
            <span>
              <img src={iconBackButton} alt="back button" className="back-button" />{' '}
              <span className="back-button-text">{t('documents.document-back-button')}</span>
            </span>
          </div>
          {!isLastIndex && (
            <div className="next-container" onClick={seeNextApproval}>
              <span className="back-button-text"> {t('documents.document-next-button')} </span>
              &nbsp;&nbsp;
              <span>
                <img src={iconNextButton} alt="back button" className="back-button" />{' '}
              </span>
            </div>
          )}
          <Tabs defaultActiveKey="document" id="uncontrolled-tab-example">
            <Tab eventKey="document" title={<span>{t('approvals.approve-document')}</span>}>
              <div className="document-preview-header">
                <div className="data-group">
                  <img src={squareEyeBox} alt="Eye" />
                  <label className="document-preview-title">{t('approvals.approver-read')}</label>
                </div>
                <div className="data-group">
                  <img src={squareDocBox} alt="Doc" />
                  <label className="document-preview-name">{selectedApproval.FileName}</label>
                </div>
                <label className="check-container">
                  {t('approvals.select')}
                  <input onChange={onCheck} type="checkbox" checked={isApprovalSelected} />
                  <div className="checkmark" />
                </label>
                <span className="document-preview-count">
                  {actualPage}/{totalPages}
                </span>
              </div>
              <div className="document-preview-container">
                <CustomModal
                  show={showSignModal}
                  handleHide={handleClose}
                  title={t('documents.cert-type-selection')}
                  singleButton
                >
                  <ListGroup defaultActiveKey="#link1">{certificatesList}</ListGroup>
                </CustomModal>
                <CustomModal
                  show={showRejectModal}
                  handleHide={handleClose}
                  handleConfirm={rejectDoc}
                  title={t('documents.reject-document')}
                  disabledButton={isInvalidRejectionReason}
                >
                  <p>{t('documents.reject-document-question')}</p>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>{t('documents.reject-reason')}</InputGroup.Text>
                    </InputGroup.Prepend>
                    <FormControl
                      as="textarea"
                      maxLength="450"
                      placeholder={t('documents.reject-reason-placeholder')}
                      value={rejectReason || ''}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="reject-reason"
                    />
                  </InputGroup>
                  {isInvalidRejectionReason && (
                    <span className="rejection-error">
                      {t('documents.rejection-reason-validation')}
                    </span>
                  )}
                </CustomModal>

                <SigningProcessModal
                  showModal={loadingApprovalProcess}
                  showProcess
                  signedDocs={0}
                  selectedDocs={1}
                  isApprovals
                />

                <RejectingProcessModal show={loadingRejectingProcess} />

                <div className="pdf-container">{images}</div>
              </div>

              <div className="buttons-container">
                <Button
                  type="button"
                  id="rejectModalbutton"
                  className="action-button reject"
                  onClick={() => setShowRejectModal(true)}
                >
                  {t('general.reject')}
                </Button>
                <Button
                  type="button"
                  id="signModalButton"
                  className="btn btn-primary action-button capitalize"
                  onClick={handleSign}
                >
                  {t('approvals.approve')}
                </Button>
              </div>
            </Tab>

            <Tab eventKey="signers" title={<span>{t('documents.document-tab-signers')}</span>}>
              <div className="signers-table-container">
                <ToolkitProvider
                  keyField="GUID"
                  data={documentSigners}
                  columns={columns}
                  bootstrap4
                >
                  {(tableProps) => (
                    <div>
                      <BootstrapTable
                        hover
                        striped
                        bordered={false}
                        {...tableProps.baseProps}
                      />
                    </div>
                  )}
                </ToolkitProvider>
              </div>
            </Tab>

            <Tab
              eventKey="attachments"
              title={
                <span>
                  <img src={iconAttachedDocs} alt="" className="tab-icons" />{' '}
                  {t('documents.document-tab-attachments')}
                </span>
              }
              tabClassName={documentAttatchmentsList.length === 0 ? 'tab-hide' : ''}
            >
              <Tab.Container defaultActiveKey={tabKey} onSelect={(k) => changeAttachedTab(k)}>
                <Row bsPrefix="attachments-row row">
                  <Col sm={3} bsPrefix="left-side-menu-col col">
                    <Nav variant="pills" className="flex-column">
                      {attDocArray}
                    </Nav>
                  </Col>
                  <Col sm={9} bsPrefix="right-side-menu-col col">
                    <Tab.Content bsPrefix="tab-content tab-content3">
                      <Tab.Pane eventKey={tabKey}>
                        {documentAttatchmentsList.length > 0 && (
                          <div className="attachment-container">
                            <div className="document-preview-container">
                              <div className="document-preview-header-2">
                                <h1 className="document-preview-title">
                                  {t('documents.document-attachment-preview-title')}
                                </h1>
                                <div className="document-preview-subcontainer">
                                  <h2 className="document-preview-name">
                                    {documentAttatchmentsList[tabKey].FileName}
                                  </h2>
                                  <span className="document-preview-count">
                                    {actualAttachedPage}/{attachedImages.length}
                                  </span>
                                </div>
                              </div>
                              <div className="pdf-container-2" id="pdf-container-2-id">
                                {attachedImages}
                              </div>
                            </div>
                          </div>
                        )}
                        {documentAttatchmentsList.length === 0 && (
                          <div className="document-preview-container">
                            <p>No hay documentos adjuntos</p>
                          </div>
                        )}
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </Tab>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(ApprovalPreview);

